// @tennis-matches/services/governance
// App-Marketplace: Rollenfilter für Minderjährige + Vereinsfrage bei Spieler-Rolle.

import { getAgeVerificationStatus } from "../trainerstunden/age-verification.js";

/** Zusätzliche Rollen, die Minderjährige im Marketplace beantragen dürfen (neben Spieler). */
export const MINOR_MARKETPLACE_EXTRA_ROLE_IDS = ["team_captain"];

/**
 * Rollen-IDs, die Minderjährige im Marketplace sehen dürfen (inkl. Spieler).
 * @type {readonly string[]}
 */
export const MINOR_MARKETPLACE_ROLE_IDS = ["player", ...MINOR_MARKETPLACE_EXTRA_ROLE_IDS];

const MINOR_ROLE_SET = new Set(MINOR_MARKETPLACE_ROLE_IDS.map((r) => String(r).toLowerCase()));

/**
 * @param {unknown} person
 * @returns {boolean}
 */
export function isMinorMarketplacePerson(person) {
  return getAgeVerificationStatus(person) === "minor";
}

/**
 * @param {readonly { role: string }[]} roleOptions
 * @param {{ isMinor?: boolean }} [ctx]
 * @returns {typeof roleOptions}
 */
export function filterMarketplaceRoleOptionsForUser(roleOptions, { isMinor = false } = {}) {
  const list = Array.isArray(roleOptions) ? roleOptions : [];
  if (!isMinor) return list;
  return list.filter((opt) => MINOR_ROLE_SET.has(String(opt?.role || "").toLowerCase()));
}

/**
 * Bereits zugewiesene/ausstehende Rollen bleiben sichtbar, auch wenn sie für Minderjährige
 * nicht neu beantragbar sind (z. B. Elternteil).
 * @param {readonly { role: string }[]} roleOptions
 * @param {readonly { role: string }[]} allRoleOptions
 * @param {unknown} lockedOrPendingRoles
 */
export function mergeLockedMarketplaceRoleOptions(roleOptions, allRoleOptions, lockedOrPendingRoles) {
  const base = Array.isArray(roleOptions) ? roleOptions : [];
  const all = Array.isArray(allRoleOptions) ? allRoleOptions : [];
  const keep = new Set(
    (lockedOrPendingRoles instanceof Set
      ? Array.from(lockedOrPendingRoles)
      : Array.isArray(lockedOrPendingRoles)
        ? lockedOrPendingRoles
        : []
    ).map((r) => String(r || "").toLowerCase())
  );
  if (!keep.size) return base;
  const seen = new Set(base.map((o) => String(o?.role || "").toLowerCase()));
  const merged = [...base];
  for (const opt of all) {
    const role = String(opt?.role || "").toLowerCase();
    if (!role || !keep.has(role) || seen.has(role)) continue;
    seen.add(role);
    merged.push(opt);
  }
  return merged;
}

/**
 * @param {unknown} roles – Set, Array oder einzelne Rolle
 * @returns {boolean}
 */
export function marketplaceRolesIncludePlayer(roles) {
  const list = roles instanceof Set ? Array.from(roles) : Array.isArray(roles) ? roles : [roles];
  return list.some((r) => String(r || "").toLowerCase() === "player");
}

/**
 * i18n-Key für Vereinsfrage (Q2) abhängig von gewählten/zugewiesenen Rollen.
 * @param {unknown} roles
 * @returns {"onboarding.q2.titlePlayer"|"onboarding.q2.title"}
 */
export function resolveMarketplaceClubQuestionKey(roles) {
  return marketplaceRolesIncludePlayer(roles) ? "onboarding.q2.titlePlayer" : "onboarding.q2.title";
}

/**
 * Self-Access-Requests: nur jugend-taugliche Rollen (Tamper-Schutz Server).
 * @param {unknown} roles
 * @returns {string[]}
 */
export function filterSelfAccessRolesForMinorMarketplace(roles) {
  const list = Array.isArray(roles) ? roles : [];
  const out = [];
  const seen = new Set();
  for (const r of list) {
    const x = String(r || "").toLowerCase().trim();
    if (!x || !MINOR_ROLE_SET.has(x) || seen.has(x)) continue;
    seen.add(x);
    out.push(x);
  }
  return out;
}
